/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import React, { Fragment } from 'react'
import { NavItem, NavLink, Nav, TabContent, TabPane, Spinner } from 'reactstrap'
import classnames from 'classnames'
import { ICoverageDiscoveryResponseStateManager, IBaseResponse, IBaseResponseDetail } from '../../../services/CoverageDiscoveryResponseStateManager/contracts'
import { BaseSectionComponent } from '../BaseSection/BaseSection'
import { CoverageDiscoveryPendingComponent } from '../CoverageDiscoveryPendingComponent/CoverageDiscoveryPendingComponent'
import { TransactionStatus } from '../../../configuration/Enums/TransactionStaus'
import { ICoverageDiscoveryTransaction } from '../../../services/CoverageDiscoveryTransactionStateManager/contracts'
import { TabTooltipComponent } from '../TabTooltip/TabTooltip'
import { SettingsManager } from '../../../configuration/SettingsManager'
import { Link } from 'react-router-dom'

export interface IBaseResponseProps {
	cdTransaction?: ICoverageDiscoveryTransaction
	baseResponseDetail: IBaseResponseDetail[]
	CoverageDiscoveryResponseStateManager: ICoverageDiscoveryResponseStateManager
}

interface IBaseResponseState {
	cdTransactionStatus?: number
	activeTab: number | null
	activeBaseResponse: IBaseResponseDetail | null
	activeResponse: IBaseResponse | null
	payerList: string[]
	IsPageLoading: boolean
}

export class BaseResponseComponent extends React.Component<IBaseResponseProps, IBaseResponseState> {
	constructor(props: Readonly<IBaseResponseProps>) {
		super(props)
		const baseResponses = this.props.baseResponseDetail

		this.state = {
			activeTab: typeof baseResponses !== 'undefined' && baseResponses !== null && baseResponses.length > 0 ? baseResponses[0].id : null,
			activeBaseResponse: typeof baseResponses !== 'undefined' && baseResponses !== null && baseResponses.length > 0 ? baseResponses[0] : null,
			activeResponse: null,
			payerList: [],
			cdTransactionStatus: this.props.cdTransaction && this.props.cdTransaction.statusId,
			IsPageLoading: true,
		}
	}
	toggle(eligibilityResponseId: number, baseResponse: IBaseResponseDetail): void {
		if (this.state.activeTab === eligibilityResponseId) return
		if (this.state.activeBaseResponse !== baseResponse) {
			this.setState({ IsPageLoading: true, activeTab: eligibilityResponseId })
			this.props.CoverageDiscoveryResponseStateManager.getResponse(eligibilityResponseId).then(response => {
				this.setState({
					activeResponse: response,
					activeBaseResponse: baseResponse,
					IsPageLoading: false,
				})
			})
		}
	}

	componentDidMount(): void {
		const baseResponses = this.props.baseResponseDetail
		if (typeof baseResponses === 'undefined' || baseResponses === null || baseResponses.length === 0) {
			this.setState({
				IsPageLoading: false,
			})
			return
		}

		this.getResponseById(this.state.activeTab)
	}

	getResponseById(responseID: number | null): void {
		if (responseID) {
			this.props.CoverageDiscoveryResponseStateManager.getResponse(responseID).then(response => {
				this.setState({
					activeResponse: response,
					IsPageLoading: false,
				})
			})
		}
	}

	renderTabs(baseResponses: IBaseResponseDetail[]): JSX.Element {
		return (
			<Fragment>
				<Nav tabs>{this.renderTabNavItems(baseResponses)}</Nav>
				{this.state.IsPageLoading && (
					<div>
						<div className="row  tab-content">
							<div className="spinnerPage">
								<Spinner animation="border" role="status" />
							</div>
						</div>
					</div>
				)}
				{!this.state.IsPageLoading && (
					<TabContent activeTab={this.state.activeTab !== null ? this.state.activeTab.toString() : ''} style={{ width: '97.8%', border: '1px solid #d8d8d8', borderTop: 'none' }}>
						{baseResponses.map((item, index) => {
							return (
								<TabPane key={index} tabId={item.id.toString()}>
									<TabPane>{this.state.activeTab === item.id && this.state.activeResponse ? <BaseSectionComponent cdTransaction={this.props.cdTransaction} response={this.state.activeResponse} CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} /> : null}</TabPane>
								</TabPane>
							)
						})}
					</TabContent>
				)}
			</Fragment>
		)
	}

	renderOther(): JSX.Element | undefined {
		return this.props.cdTransaction && <CoverageDiscoveryPendingComponent transactionRequestDetails={this.props.cdTransaction.transactionRequest.individual} transactionRequestGuarantorDetails={this.props.cdTransaction.transactionRequest.guarantor} cdTransactionStatus={this.state.cdTransactionStatus} transactionId={this.props.cdTransaction.id} demographic={this.props.cdTransaction.transactionResponse && this.props.cdTransaction.transactionResponse.demographic}></CoverageDiscoveryPendingComponent>
	}

	render(): React.ReactNode {
		const baseResponses = this.props.baseResponseDetail
		if (typeof baseResponses === 'undefined' || baseResponses === null || baseResponses.length === 0 || TransactionStatus.Complete !== this.state.cdTransactionStatus) {
			return this.renderOther()
		}
		if (TransactionStatus.Complete === this.state.cdTransactionStatus) {
			const cdTransaction = this.props.cdTransaction
			return (
				<div className="response-content row  tab-content">
					<div className="width100P">
						{cdTransaction && cdTransaction.statusId !== TransactionStatus.Processing && (
							<div className="text-right">
								<Link to={`/CoverageDiscoveryRequest/${cdTransaction && cdTransaction.id}`}>RERUN</Link>
							</div>
						)}
						<div className="contentBody  cd-response">
							<div className="row-fluid">{this.renderTabs(baseResponses)}</div>
						</div>
					</div>
				</div>
			)
		}
	}
	renderTabNavItems(baseResponses: IBaseResponseDetail[]): any {
		const payerRankToShow = SettingsManager.RESPONSE_PAGE_MAX_PAYERS_RANK_TO_SHOW

		return baseResponses.map((baseResponse, index) => {
			const rankDetail = index < payerRankToShow && baseResponse.hierarchy ? ' (' + baseResponse.hierarchy + ')' : ''

			const payerName = baseResponse.payer.name + rankDetail

			return (
				<NavItem key={index} className={classnames({ active: this.state.activeTab === baseResponse.id })}>
					<NavLink
						className={classnames({ active: this.state.activeTab === baseResponse.id })}
						onClick={(): void => {
							this.toggle(baseResponse.id, baseResponse)
						}}
					>
						{this.loadPayerNameWithTooltip(payerName, baseResponse.id)}
					</NavLink>
				</NavItem>
			)
		})
	}

	private loadPayerNameWithTooltip(payerName: string, responseId: number): JSX.Element {
		const payerNameMaxLength = SettingsManager.RESPONSE_PAGE_TAB_PAYERNAME_MAXLENGTH

		return (
			<div className="response-tabs">
				{payerName.length > payerNameMaxLength ? payerName.substring(0, payerNameMaxLength) + '...' : payerName} {payerName.length > payerNameMaxLength && <TabTooltipComponent targetId={'NavTab' + responseId} toolTipContent={payerName} />}
			</div>
		)
	}
}
